import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { List, Map } from 'immutable';
import { padStart } from 'lodash';
import {
  ISearchParams,
  SortByEnum,
  SortOrderEnum,
} from '../../../entity-data/entity-data.data';
import { IMasterListBuilder } from '../../../entity-master/components/master-list/master-list-builder.interface';
import { IMasterListHeader } from '../../../entity-master/components/master-list/master-list.interface';
import { IMetadata } from '../../../feed/feed.interface';
import { FriendlyDurationPipe } from '../../../shared/pipes/friendly-duration/friendly-duration.pipe';
import { TrackedTime } from '../../../tracked-time-data/models/tracked-time.model';
import { I2gNgTrackingActionBuilder } from 'i2g-ng-tracking';
import { DurationPerYearFilter } from '../../../tracked-time-data/models/duration-per-year-filter.model';
import { ISelectOption } from '../../../form-master/components/select-field/select-field.interface';
import { TotalPerClientFilter } from '../../../tracked-time-data/models/total-per-client-filter.model';
import { TotalPerMonthFilter } from '../../../tracked-time-data/models/total-per-month-filter.model';
import {
  ITrackingObject,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { DateTimeService, MOMENT_MONTH_YEAR_DATE_FORMAT } from 'i2g-ng-utils';

interface ITotalPerMonth {
  key: string;
  value: number; // duration
}

@Injectable()
export class TrackedTimeListBuilder implements IMasterListBuilder {
  constructor(
    private _translateService: TranslateService,
    private _dateTimeService: DateTimeService,
    private _friendlyDurationPipe: FriendlyDurationPipe,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder
  ) {}

  buildListHeaders(
    trackingObject: ITrackingObject,
    trackingScreenName: TrackingScreenNamesEnum
  ): List<IMasterListHeader> {
    return List([
      {
        id: 'tracked-time-client-name',
        label: this._translateService.instant(
          'TRACKED_TIME.LIST_HEADERS.CLIENT'
        ),
        customClass: 'tracked-time-client-name',
        sortBy: SortByEnum.client_name,
        sortOrder: SortOrderEnum.asc,
        trackingObject,
        trackingAction: this._trackingActionBuilder.buildButtonTapped(
          'sort_client',
          trackingScreenName
        ),
      },
      {
        id: 'tracked-time-start-date',
        label: this._translateService.instant('TRACKED_TIME.LIST_HEADERS.DATE'),
        customClass: 'tracked-time-start-date',
        sortBy: SortByEnum.start_date,
        sortOrder: SortOrderEnum.desc,
        trackingObject,
        trackingAction: this._trackingActionBuilder.buildButtonTapped(
          'sort_date',
          trackingScreenName
        ),
      },
      {
        id: 'tracked-time-notes',
        label: this._translateService.instant(
          'TRACKED_TIME.LIST_HEADERS.NOTES'
        ),
        customClass: 'tracked-time-notes',
      },
      {
        id: 'tracked-time-duration',
        label: this._translateService.instant(
          'TRACKED_TIME.LIST_HEADERS.DURATION'
        ),
        customClass: 'tracked-time-duration',
      },
    ]);
  }

  buildSortByHeader(params: ISearchParams): IMasterListHeader {
    return {
      sortBy: params.sort_by as SortByEnum,
      sortOrder: params.sort_order as SortOrderEnum,
    };
  }

  buildYearFilterOptions(
    filters: List<DurationPerYearFilter>
  ): List<ISelectOption> {
    return filters.map(
      (filter: DurationPerYearFilter): ISelectOption => {
        return filter.isAllYears()
          ? {
              key: this._translateService.instant('LABEL.ALL_YEARS'),
              value: filter,
              trackingValue: 'filter_all_years',
            }
          : {
              key: filter.year,
              value: filter,
              trackingValue: `filter_${filter.year}`,
            };
      }
    ) as List<ISelectOption>;
  }

  buildGroupedTotals(
    header: IMasterListHeader,
    filters: List<DurationPerYearFilter>,
    selectedFilter: DurationPerYearFilter
  ) {
    return header.sortBy === SortByEnum.client_name
      ? this.buildClientGroupedTotals(selectedFilter)
      : this.buildMonthlyGroupedTotals(filters, selectedFilter);
  }

  buildClientGroupedTotals(
    selectedFilter: DurationPerYearFilter
  ): Map<string, IMetadata> {
    const groupMetadata: any = selectedFilter.clients.reduce(
      (
        result: Map<string, IMetadata>,
        totalPerClient: TotalPerClientFilter
      ) => {
        return result.set(totalPerClient.id, {
          label: totalPerClient.name,
          data: this._friendlyDurationPipe.transform(totalPerClient.duration),
        } as IMetadata);
      },
      Map<string, IMetadata>({})
    );

    return groupMetadata;
  }

  buildMonthlyGroupedTotals(
    filters: List<DurationPerYearFilter>,
    selectedFilter: DurationPerYearFilter
  ): Map<string, IMetadata> {
    // map monthly totals to formatted date and build ALL_YEARS group
    const months = selectedFilter.isAllYears()
      ? filters.reduce(
          (result: List<ITotalPerMonth>, filter: DurationPerYearFilter) => {
            return result.concat(
              filter.months.map((totalPerMonth) =>
                this._monthToFormattedDateMapper(totalPerMonth)
              )
            );
          },
          List<ITotalPerMonth>([])
        )
      : selectedFilter.months.map((totalPerMonth) =>
          this._monthToFormattedDateMapper(totalPerMonth)
        );

    return months
      .reduce((result: Map<string, number>, totalPerMonth: ITotalPerMonth) => {
        return result.set(
          totalPerMonth.key,
          result.get(totalPerMonth.key, 0) + totalPerMonth.value
        );
      }, Map<string, number>({}))
      .map((totalDuration: number, date: string) => {
        return {
          label: date,
          data: this._friendlyDurationPipe.transform(totalDuration),
        };
      }) as Map<string, IMetadata>;
  }

  buildGroupByFn(sortBy: SortByEnum): (trackedTime: TrackedTime) => any {
    switch (sortBy) {
      case SortByEnum.start_date:
        return (trackedTime: TrackedTime) => {
          const { year, month } = this._dateTimeService.parse(
            trackedTime.startDate
          );

          return this._formatDate(year, month + 1);
        };
      case SortByEnum.client_name:
        return (trackedTime: TrackedTime) => trackedTime.clientReference.id;
      default:
        return null;
    }
  }

  private _formatDate(year: number, month: number, day?: number): string {
    const mm: string = padStart(month.toString(10), 2, '0');
    const dd: string = padStart((day || 1).toString(10), 2, '0');

    return this._dateTimeService.formatDate(
      `${year}-${mm}-${dd}`,
      MOMENT_MONTH_YEAR_DATE_FORMAT
    );
  }

  private _monthToFormattedDateMapper(
    totalPerMonth: TotalPerMonthFilter
  ): ITotalPerMonth {
    return {
      key: this._formatDate(totalPerMonth.year, totalPerMonth.month),
      value: totalPerMonth.duration,
    };
  }
}
